import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {CodonTranslationTable, NucleotideType} from 'biojs/BioConstants';
import DataConnector from 'connect/DataConnector';
import {convertStrandToDisplayName, StrandOfDoubleStranded} from 'constants/MolbioConstants';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import AminoAcidProperties from 'molbio/aa-sequences/AminoAcidProperties';
import * as sequtil from 'molbio/SeqUtil';
import {residueSlice} from 'molbio/SeqUtil';
import GeneticCodeDropdown from 'molbio/translations/GeneticCodeDropdown';
import {proteinSelectionUIGlobalState} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import ToggleGroup, {ToggleGroupOption} from 'ui-library/ToggleGroup';

function formatRangeForStrand(
  displayStart: number,
  displayEnd: number,
  strand: StrandOfDoubleStranded
): string {
  if (strand === 1) {
    return `${displayStart}-${displayEnd}`;
  } else {
    return `${displayEnd}-${displayStart}`;
  }
}

interface StopCodonPosition {
  // Leftmost base pair index, regardless of direction (inclusive).
  stopCodonStart: number;
  // Rightmost base pair index, regardless of direction (exclusive).
  stopCodonEnd: number;
}

/**
 * Returns amino acids that occur before a stop codon, whether a stop codon
 * exists in the inputted aminoAcids string, and updated start and end indices.
 * Only relevant when polymer type is DNA.
 */
export function getAminoAcidsBeforeStopCodon(
  aminoAcids: string,
  start: number,
  end: number,
  strand: StrandOfDoubleStranded,
  sequenceLength: number
): {aminoAcids: string; stopCodonPosition: StopCodonPosition | null; start: number; end: number} {
  let aminoAcidsBeforeStopCodon = aminoAcids;
  let stopCodonPosition = null;

  const firstStopCodonIndex = aminoAcids.indexOf('*');
  if (firstStopCodonIndex !== -1) {
    aminoAcidsBeforeStopCodon = aminoAcids.slice(0, firstStopCodonIndex);

    if (strand === 1) {
      end = sequtil.mod(firstStopCodonIndex * 3 + start, sequenceLength);
      stopCodonPosition = {stopCodonStart: end, stopCodonEnd: sequtil.mod(end + 3, sequenceLength)};
    } else {
      start = sequtil.mod(end - firstStopCodonIndex * 3, sequenceLength);
      stopCodonPosition = {stopCodonStart: sequtil.mod(start - 3, sequenceLength), stopCodonEnd: start};
    }
  }

  return {aminoAcids: aminoAcidsBeforeStopCodon, stopCodonPosition, start, end};
}

function getSummaryAminoAcids(aminoAcids: string, includesStopCodon: boolean): string {
  const stopCodon = includesStopCodon ? '*' : '';
  if (aminoAcids.length <= 8) {
    return `${aminoAcids}${stopCodon}`;
  }
  return `${aminoAcids.slice(0, 4)}...${aminoAcids.slice(aminoAcids.length - 4)}${stopCodon}`;
}

function ErrorMessage({children}: {children: ReactNode}): JSX.Element {
  return (
    <div className="container biochemicalPropertiesContainer">
      <Row>
        <Col md={10}>
          <div
            className="alert alert-warning u-spacing-top"
            {...getTestProps({component: 'BiochemicalProperties', element: 'errorMessage'})}
          >
            {children}
          </div>
        </Col>
      </Row>
    </div>
  );
}

interface StopCodonPositionMessageProps {
  stopCodonPosition: StopCodonPosition | null;
  strand: StrandOfDoubleStranded;
  startIndex: number;
  sequenceLength: number;
}

export function StopCodonPositionMessage({
  stopCodonPosition,
  strand,
  startIndex,
  sequenceLength,
}: StopCodonPositionMessageProps): JSX.Element | null {
  if (stopCodonPosition == null) {
    return null;
  }

  const displayStart = sequtil.modelToDisplay(stopCodonPosition.stopCodonStart, startIndex);
  const displayEnd = sequtil.modelEndToRender(stopCodonPosition.stopCodonEnd, sequenceLength);

  return (
    <div
      className="alert alert-info u-spacing-top"
      {...getTestProps({component: 'StopCodonPositionMessage', element: 'root'})}
    >
      Stop codon was found at position {formatRangeForStrand(displayStart, displayEnd, strand)}.
    </div>
  );
}

function getToggleGroupOptions(
  nucleotideType: NucleotideType
): Array<ToggleGroupOption<StrandOfDoubleStranded>> {
  const forwardOption: ToggleGroupOption<StrandOfDoubleStranded> = {
    id: 1,
    buttonProps: {text: convertStrandToDisplayName(1)},
  };
  const reverseOption: ToggleGroupOption<StrandOfDoubleStranded> = {
    id: -1,
    buttonProps: {text: convertStrandToDisplayName(-1)},
  };
  if (nucleotideType === NucleotideType.RNA) {
    // Disable the reverse option with an explanation.
    reverseOption.buttonProps.disabled = true;
    reverseOption.popOverProps = {
      disabled: false,
      text: 'RNA sequences are single-stranded in Benchling. They do not possess a reverse strand.',
    };
  }
  return [forwardOption, reverseOption];
}

export interface TranslationBiochemicalPropertiesProps {
  // Leftmost base pair index, regardless of direction (inclusive).
  start: number;
  // Rightmost base pair index regardless of direction (exclusive). Null/undefined if selection is a single position.
  end: number | null;
  length: number;
  startIndex: number;
  aminoAcids: string;
  sequenceStrand: StrandOfDoubleStranded;
  setSequenceStrand: (strand: StrandOfDoubleStranded) => void;
  sequenceNucleotideType: NucleotideType;
  sequenceCodonTranslationTable: CodonTranslationTable;
  setSequenceCodonTranslationTable: (codonTranslationTable: CodonTranslationTable) => void;
  coerceStartCodonToMethionine: boolean;
  setCoerceStartCodonToMethionine: (shouldCoerceStartCodonToMethionine: boolean) => void;
}

/**
 * Component displaying information about the amino acids in a translation of
 * amino acids in a selection of a sequence. We automatically ignore any amino acids
 * past a stop codon.
 */
export function TranslationBiochemicalProperties(props: TranslationBiochemicalPropertiesProps): JSX.Element {
  if (props.end == null) {
    return (
      <ErrorMessage>
        Protein biochemical properties of a region will appear here after you make a selection in the sequence
        map.
      </ErrorMessage>
    );
  }

  const {aminoAcids, stopCodonPosition, start, end} = getAminoAcidsBeforeStopCodon(
    props.aminoAcids,
    props.start,
    props.end,
    props.sequenceStrand,
    props.length
  );

  if (aminoAcids.indexOf('?') !== -1) {
    return (
      <ErrorMessage>
        {props.sequenceNucleotideType} sequence contains degenerate bases which result in unknown amino acids.
        Biochemical properties can only be shown for valid translations.
      </ErrorMessage>
    );
  }

  let position: string = formatRangeForStrand(
    sequtil.modelToDisplay(start, props.startIndex),
    sequtil.modelEndToRender(end, props.length),
    props.sequenceStrand
  );
  if (props.sequenceNucleotideType === NucleotideType.DNA) {
    position = `${position} (${convertStrandToDisplayName(props.sequenceStrand).toLowerCase()})`;
  }
  const extraProperties = [
    {
      name: 'Position',
      value: position,
      dataTestAttrs: {component: 'AminoAcidProperties', element: 'position'},
    },
    {
      name: 'Summary',
      value: `${getSummaryAminoAcids(aminoAcids, stopCodonPosition != null)} ${aminoAcids.length} AAs`,
      dataTestAttrs: {component: 'AminoAcidProperties', element: 'summary'},
    },
  ];
  return (
    <div
      className={css(styles.tabWrapper)}
      {...getTestProps({component: 'BiochemicalProperties', element: 'root'})}
    >
      <FormGroup className={classNames(css(styles.formGroup), 'u-spacing-top')}>
        <FieldLabel label="Strand" />
        <div>
          <ToggleGroup<StrandOfDoubleStranded>
            options={getToggleGroupOptions(props.sequenceNucleotideType)}
            selection={props.sequenceStrand}
            onChangeSelection={props.setSequenceStrand}
            size={ButtonSize.SMALL}
          />
        </div>
      </FormGroup>
      <FormGroup className={css(styles.formGroup, styles.geneticCodeDropdown)}>
        <FieldLabel label="Genetic Code" />
        <GeneticCodeDropdown
          geneticCode={props.sequenceCodonTranslationTable}
          onSelectGeneticCode={props.setSequenceCodonTranslationTable}
        />
      </FormGroup>
      <FormGroup className={css(styles.formGroup)}>
        <CheckboxInput
          label="Translate start codon as methionine"
          id="coerce-start-codon-as-methionine"
          checked={props.coerceStartCodonToMethionine}
          onChange={(e) => {
            props.setCoerceStartCodonToMethionine(e.target.checked);
          }}
        />
      </FormGroup>
      <AminoAcidProperties
        aminoAcids={aminoAcids}
        alertMessage={
          <StopCodonPositionMessage
            stopCodonPosition={stopCodonPosition}
            strand={props.sequenceStrand}
            startIndex={props.startIndex}
            sequenceLength={props.length}
          />
        }
        extraProperties={extraProperties}
      />
    </div>
  );
}

class ProteinBiochemicalPropertiesConnector extends DataConnector<
  {proteinId: string},
  ProteinBiochemicalPropertiesEnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [AaSequenceStore];

  enhanceProps(): ProteinBiochemicalPropertiesEnhancedProps {
    const aaSequenceStore = this.aaSequenceStore();
    const aaSequence = aaSequenceStore.getAaSequenceById(this.props.proteinId)!;
    return {aminoAcids: aaSequence.version.aminoAcids};
  }
}

interface ProteinBiochemicalPropertiesEnhancedProps {
  aminoAcids: string;
}

export interface ProteinBiochemicalPropertiesProps extends ProteinBiochemicalPropertiesEnhancedProps {
  proteinId: string;
}

/**
 * Component displaying information about the amino acids in a selection of
 * amino acids in a larger AA sequence.
 */
export function ProteinBiochemicalProperties({
  proteinId,
  aminoAcids,
}: ProteinBiochemicalPropertiesProps): JSX.Element {
  const [proteinSelectionState] = proteinSelectionUIGlobalState.useGlobalClientState(proteinId);
  const start = proteinSelectionState.end == null ? 0 : proteinSelectionState.start;
  const end = proteinSelectionState.end == null ? 0 : proteinSelectionState.end;
  const selectedAminoAcids = residueSlice(aminoAcids, start, end, false);

  return (
    <div
      className={css(styles.tabWrapper)}
      {...getTestProps({component: 'BiochemicalProperties', element: 'root'})}
    >
      <AminoAcidProperties
        aminoAcids={selectedAminoAcids}
        extraProperties={[
          {
            name: 'Position',
            value: `${sequtil.modelToDisplay(start, 1)}-${sequtil.modelEndToRender(end, aminoAcids.length)}`,
            dataTestAttrs: {component: 'AminoAcidProperties', element: 'position'},
          },
          {
            name: 'Summary',
            value: `${getSummaryAminoAcids(selectedAminoAcids, false)} ${selectedAminoAcids.length} AAs`,
            dataTestAttrs: {component: 'AminoAcidProperties', element: 'summary'},
          },
        ]}
      />
    </div>
  );
}

export const ConnectedProteinBiochemicalProperties = ProteinBiochemicalPropertiesConnector.connect(
  ProteinBiochemicalProperties
);

const styles = StyleSheet.create({
  tabWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  formGroup: {
    textAlign: 'center',
  },
  geneticCodeDropdown: {
    width: '256px',
  },
});
